<template>
  <div v-show="tracks.length" ref="panel" :class="[
    'transcripts',
    collapsed && 'is--collapsed'
  ]" :style="panelStyle">
    <div class="transcripts__handle" @pointerdown="dragStart">
      <span class="transcripts__grip">
        <span /><span /><span />
      </span>
      <span class="transcripts__title">{{ $t('transcript.header') }}</span>
      <button type="button" class="transcripts__toggle" @click="collapsed = !collapsed">
        {{ collapsed ? '▲' : '▼' }}
      </button>
    </div>

    <!-- collapsed: single line, latest cue only -->
    <div v-if="collapsed" class="transcripts__line">
      {{ latestLine || '...' }}
    </div>

    <!-- expanded: full history per track, ordered top -> bottom -->
    <div v-else ref="body" class="transcripts__body">
      <div v-for="track in tracks" :key="track.id" class="transcripts__track">
        <div class="transcripts__history">
          <p v-for="entry in track.history" :key="entry.id" class="transcripts__entry">
            {{ entry.text }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { procedures } from '@communication'
import { ref, reactive, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

const body = ref(null)

const collapsed = ref(false)
const tracks = reactive([])

const position = reactive({ x: 24, y: 300 })
const dragging = reactive({ active: false, startX: 0, startY: 0, originX: 0, originY: 0 })

const panelStyle = computed(() => ({
  left: `${position.x}px`,
  top: `${position.y}px`
}))

const totalLines = computed(() => tracks.reduce((sum, track) => sum + track.history.length, 0))

async function scrollToEnd() {
  await nextTick()
  if (body.value) {
    body.value.scrollTop = body.value.scrollHeight
  }
}

watch(totalLines, scrollToEnd)
watch(collapsed, (value) => { if (!value) scrollToEnd() })

const latestLine = computed(() => {
  const withText = tracks.filter((t) => t.current)
  if (!withText.length) return ''
  const last = withText.reduce((a, b) => (a.updatedAt > b.updatedAt ? a : b))
  return `${last.label}: ${last.current}`
})

function dragStart(event) {
  dragging.active = true
  dragging.startX = event.clientX
  dragging.startY = event.clientY
  dragging.originX = position.x
  dragging.originY = position.y
  window.addEventListener('pointermove', dragMove)
  window.addEventListener('pointerup', dragEnd)
}

function dragMove(event) {
  if (!dragging.active) return
  position.x = dragging.originX + (event.clientX - dragging.startX)
  position.y = dragging.originY + (event.clientY - dragging.startY)
}

function dragEnd() {
  dragging.active = false
  window.removeEventListener('pointermove', dragMove)
  window.removeEventListener('pointerup', dragEnd)
}

let historySeq = 0

function trackStateFor(textTrack) {
  let state = tracks.find((t) => t.ref === textTrack)
  if (!state) {
    state = reactive({
      id: `${textTrack.label}-${tracks.length}`,
      ref: textTrack,
      label: textTrack.label || `Speaker ${tracks.length + 1}`,
      current: '',
      updatedAt: 0,
      history: [],
      cueIndex: new Map()
    })
    tracks.push(state)
  }
  return state
}

function onCueChange(textTrack) {
  const state = trackStateFor(textTrack)
  const activeCues = textTrack.activeCues || []
  for (const cue of activeCues) {
    let entry = state.cueIndex.get(cue)
    if (!entry) {
      entry = reactive({ id: historySeq++, text: cue.text })
      state.cueIndex.set(cue, entry)
      state.history.push(entry)
    } else {
      entry.text = cue.text
    }
    state.current = cue.text
    state.updatedAt = Date.now()
  }
  scrollToEnd()
}

onMounted(() => {
  const liveCaptionProcedure = procedures.get('liveCaptionProcedure')
  liveCaptionProcedure.on('cueChanged', (e) => onCueChange(e.detail))
})

onBeforeUnmount(() => {
  window.removeEventListener('pointermove', dragMove)
  window.removeEventListener('pointerup', dragEnd)
})
</script>

<style lang="scss">
.transcripts {
  position: fixed;
  z-index: $zindex-tools;
  width: 320px;
  max-width: calc(100vw - 32px);
  max-height: 45vh;
  display: flex;
  flex-direction: column;
  background: rgba(0, 0, 0, 0.3);
  color: $white;
  border-radius: $border-radius;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(2px);

  &__video {
    display: none;
  }

  &__handle {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    cursor: grab;
    background: rgba(255, 255, 255, 0.08);
    user-select: none;
    touch-action: none;

    &:active {
      cursor: grabbing;
    }
  }

  &__grip {
    display: flex;
    flex-direction: column;
    gap: 2px;

    span {
      width: 4px;
      height: 4px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.6);
    }
  }

  &__title {
    flex: 1;
    font-size: 12px;
    font-weight: 600;
    opacity: 0.8;
  }

  &__toggle {
    background: transparent;
    border: none;
    color: $white;
    cursor: pointer;
    font-size: 10px;
    line-height: 1;
    padding: 4px;
  }

  &__line {
    padding: 8px 10px;
    font-size: 13px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__body {
    overflow-y: auto;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__entry {
    margin: 0 0 4px;
    font-size: 13px;
    line-height: 1.4;
  }

  &.is--collapsed {
    width: 260px;
  }
}
</style>
